// Per-item download. The presigned R2 URL is a direct GET, so a plain anchor with
// `download` works — but a cross-origin `download` attribute is advisory (the R2
// host may not echo Content-Disposition), so we fetch the bytes and save a Blob
// URL, which reliably names the file. No bytes pass through the Worker either way
// (ADR-0022): the fetch hits R2's presigned URL directly.

import type { MediaItem } from "./api";
import { buildZip, isClip, type ZipEntry } from "./cores";

/** Derive a friendly filename from an R2 key ("{userId}/{deviceId}/IMG_0007.bmp" → "IMG_0007.bmp"). */
export function filenameFor(item: Pick<MediaItem, "key">): string {
  const tail = item.key.split("/").pop();
  return tail && tail.length > 0 ? tail : "pixie-media";
}

/**
 * A human label for a media item — "Capture #7" / "Clip #3" — from the `####` index in its filename
 * (a Clip is a GIF, a Capture a still BMP, per `isClip`). Falls back to the raw filename if there's
 * no number. The real filename stays the download name + the card's tooltip.
 */
export function mediaLabel(item: Pick<MediaItem, "key">): string {
  const name = filenameFor(item);
  const m = name.match(/(\d+)/);
  if (!m) return name;
  return `${isClip(item.key) ? "Clip" : "Capture"} #${parseInt(m[1]!, 10)}`;
}

/** Injectable seam so the download flow is testable without a real DOM/network. */
export interface DownloadDeps {
  fetch?: typeof fetch;
  /** Triggers the actual save (anchor click). Default uses a temporary <a>. */
  saveBlob?: (blob: Blob, filename: string) => void;
}

/**
 * Download a media item to disk. Fetches the bytes straight from the presigned R2
 * URL and saves them under the item's filename. Throws if the fetch fails so the
 * caller can surface an error.
 */
export async function downloadItem(
  item: MediaItem,
  deps: DownloadDeps = {},
): Promise<void> {
  const doFetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const res = await doFetch(item.url);
  if (!res.ok) {
    throw new Error(`download failed (${res.status})`);
  }
  const blob = await res.blob();
  (deps.saveBlob ?? saveBlobViaAnchor)(blob, filenameFor(item));
}

/** What a ZIP download actually did — the caller reports skips honestly (ADR-0053 spirit). */
export interface ZipReport {
  zipped: number;
  skipped: number;
}

/**
 * Download several media items as a single .zip. Fetches each item's bytes straight
 * from its presigned R2 URL (in parallel; no bytes through the Worker — ADR-0022),
 * bundles them STORE-method into one archive (`buildZip`), and saves it.
 *
 * Per-item failures (e.g. ONE expired presigned URL in a big selection) no longer
 * abort the whole batch: the successful files still zip and the report carries the
 * skip count — the same per-file skip philosophy the firmware Sync uses (ADR-0053).
 * Throws only when EVERY item failed (nothing to save), so the caller can surface a
 * real error and leave the selection intact.
 */
export async function downloadZip(
  items: MediaItem[],
  deps: DownloadDeps & { zipName?: string } = {},
): Promise<ZipReport> {
  const doFetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const settled = await Promise.allSettled(
    items.map(async (item): Promise<ZipEntry> => {
      const res = await doFetch(item.url);
      if (!res.ok) {
        throw new Error(`download failed for ${filenameFor(item)} (${res.status})`);
      }
      return { name: filenameFor(item), bytes: new Uint8Array(await res.arrayBuffer()) };
    }),
  );
  const entries = settled
    .filter((s): s is PromiseFulfilledResult<ZipEntry> => s.status === "fulfilled")
    .map((s) => s.value);
  if (entries.length === 0) {
    throw new Error(`download failed — none of the ${items.length} files could be read`);
  }
  const blob = new Blob([buildZip(entries)], { type: "application/zip" });
  (deps.saveBlob ?? saveBlobViaAnchor)(blob, deps.zipName ?? "pixie-media.zip");
  return { zipped: entries.length, skipped: items.length - entries.length };
}

/**
 * A per-SAVE unique filename: the base name + a compact time suffix
 * ("IMG_0486-143205.bmp"). The browser's "Download again?" duplicate warning keys on
 * repeated same-name downloads — the fixed "pixie-media.zip", the deterministic Mosaic
 * name, and a re-grabbed capture re-prompted on every save (owner report). Applied at
 * save time ONLY, so identity names (cards, tooltips, the entry names INSIDE a zip)
 * stay clean.
 */
export function uniqueDownloadName(base: string): string {
  const d = new Date();
  const t = [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, "0"))
    .join("");
  const dot = base.lastIndexOf(".");
  return dot <= 0 ? `${base}-${t}` : `${base.slice(0, dot)}-${t}${base.slice(dot)}`;
}

/** Default save: object-URL + a transient anchor click, then revoke the URL. */
function saveBlobViaAnchor(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = uniqueDownloadName(filename);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
